import { Injectable, isDevMode } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subject, filter, take } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo, SilentRequest } from '@azure/msal-browser';
import { environment } from '../../../../environments/environment';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import User from '../../../core/data/dtos/user.dto';
import { UserRegistrationRole } from '../../../core/data/new-dtos/user-registration.dtos';
import { CurrentBankUserDto } from '../../../core/data/new-dtos/current-bank-user.dtos';

/** How long the app waits for Employees/me before rendering without a profile. */
const PROFILE_READY_TIMEOUT_MS = 15000;

@Injectable()
export class AuthService {
  account?: AccountInfo;
  private $logout = new Subject<void>();
  _logout = this.$logout.asObservable();
  private $login = new Subject<void>();
  _login = this.$login.asObservable();
  private authTokenSubject = new BehaviorSubject<string | null>(null);
  authToken$ = this.authTokenSubject.asObservable();

  private bankProfileSubject = new BehaviorSubject<CurrentBankUserDto | null>(null);
  bankProfile$ = this.bankProfileSubject.asObservable();

  private profileLoadedSubject = new BehaviorSubject<boolean>(false);
  profileLoaded$ = this.profileLoadedSubject.asObservable();

  constructor(
    private router: Router,
    protected httpClient: HttpClient,
    private msalService: MsalService,
  ) {
    // Local development: impersonate a seeded user, skipping B2C entirely
    if (environment.devAuth?.enabled) {
      // Still initialise MSAL: nothing signs in, but stray calls such as the sidenav
      // logout would otherwise throw uninitialized_public_client_application.
      this.msalService.initialize().subscribe({ error: () => { } });
      this.setAuthToken('dev');

      // The layout and header render only when `user` is set, which normally comes
      // from the MSAL account. Stand in for it, then fill in the real name below.
      const devUser = localStorage.getItem('devUser') || environment.devAuth.defaultUser;
      this.user = {
        localAccountId: devUser,
        username: devUser,
        name: devUser,
        idTokenClaims: { given_name: devUser },
      } as unknown as User;

      this.logIdentity('startup');

      // Deferred: calling it here would build the HTTP interceptor chain while this
      // service is still constructing, and ErrorInterceptor injects AuthService (NG0200).
      queueMicrotask(() =>
        this.getBankProfile().then(() => {
          const profile = this.bankProfile;
          if (!profile) return;
          this.user = {
            ...this.user,
            name: `${profile.name} ${profile.lastName}`.trim(),
            idTokenClaims: { ...this.user.idTokenClaims, given_name: profile.name },
          } as unknown as User;
        }),
      );
      return;
    }

    void this.bootstrapSession();
  }

  /**
   * Brings the session up: finish any redirect still being processed, then acquire a
   * token for the signed-in account.
   *
   * This drives MSAL directly instead of waiting on `inProgress$`. That observable is a
   * plain BehaviorSubject seeded with `Startup`, and MsalBroadcastService only starts
   * listening for events when it is first constructed. Routes that are not behind
   * MsalGuard finish the redirect in their own guard (see b2cSignupGuard) — before this
   * service, and therefore the broadcast service, exists. The HANDLE_REDIRECT_END event
   * is emitted into the void, `inProgress$` sits at `Startup` forever, and no token is
   * ever acquired: every API call then goes out unauthenticated and 401s.
   */
  private async bootstrapSession(): Promise<void> {
    try {
      // Initializes MSAL as well. Safe to call even when a guard already did:
      // msal-browser memoizes it and hands back the first invocation's result.
      await lastValueFrom(this.msalService.handleRedirectObservable());
    } catch (error) {
      console.error('Failed to initialize authentication', error);
    }

    this.getAccount();
    if (!this.account) {
      // Anonymous session - guards waiting on the profile must not hang
      this.profileLoadedSubject.next(true);
      return;
    }

    await this.getToken();
  }

  get bankProfile() {
    return this.bankProfileSubject.value;
  }

  get bankIds(): number[] {
    return this.bankProfile?.bankIds ?? [];
  }

  get branchIds(): number[] {
    return this.bankProfile?.branchIds ?? [];
  }

  get azureId(): string | undefined {
    return (
      this.account?.localAccountId ||
      (this.user as any)?.localAccountId ||
      this.account?.homeAccountId
    );
  }

  /**
   * Re-reads the bank profile from the API. Used when something may have changed
   * server-side while the user waited, such as a registration being approved.
   */
  async refreshProfile(): Promise<CurrentBankUserDto | null> {
    await this.getBankProfile();
    return this.bankProfile;
  }

  /** Resolves once the bank profile fetch has settled (or the session is anonymous). */
  profileReady(): Promise<void> {
    return new Promise((resolve) => {
      // Bounded on purpose. Guards and screens await this before rendering, so a
      // profile request that never settles would leave the whole app stuck on a
      // spinner. Timing out falls back to the token claims instead of hanging.
      const bail = setTimeout(() => {
        console.warn('Timed out waiting for the bank profile; continuing without it');
        resolve();
      }, PROFILE_READY_TIMEOUT_MS);

      this.profileLoaded$
        .pipe(
          filter((loaded) => loaded),
          take(1),
        )
        .subscribe(() => {
          clearTimeout(bail);
          resolve();
        });
    });
  }

  get authToken() {
    return this.authTokenSubject.value;
  }

  private _user!: User;

  userSubject = new BehaviorSubject<User>(this._user);

  get user() {
    return this._user;
  }

  set user(user: User) {
    this._user = user;

    this.userSubject.next(user);
  }

  // Directors exist only server-side (no B2C role claim), so no token fallback
  get isGlobal() {
    return this.bankProfile?.role === 'global';
  }

  get isRegional() {
    return this.bankProfile?.role === 'regional';
  }

  get isDirector() {
    return this.isGlobal || this.isRegional;
  }

  /**
   * The role the B2C token itself claims. The user flows emit it as
   * extension_EmployeeRole with the role's name ("Manager"); extension_Role is
   * the legacy numeric spelling. The bank profile always wins when present.
   */
  private tokenRoleIs(role: UserRegistrationRole): boolean {
    const claims = this.user?.idTokenClaims as any;
    const raw = claims?.extension_EmployeeRole ?? claims?.extension_Role;

    if (raw === null || raw === undefined || raw === '') return false;
    if (typeof raw === 'number' || /^\d+$/.test(String(raw))) return Number(raw) === role;
    return String(raw).toLowerCase() === UserRegistrationRole[role].toLowerCase();
  }

  get isSupervisor() {
    if (this.bankProfile) return this.bankProfile.role === 'supervisor';
    return this.tokenRoleIs(UserRegistrationRole.Supervisor);
  }

  get isManager() {
    if (this.bankProfile) return this.bankProfile.role === 'manager';
    return this.tokenRoleIs(UserRegistrationRole.Manager);
  }

  get isRetailer() {
    if (this.bankProfile) return this.bankProfile.role === 'retailer';
    return this.tokenRoleIs(UserRegistrationRole.Retailer);
  }

  get isSuperUser() {
    if (this.bankProfile) return this.bankProfile.role === 'superuser';
    return this.tokenRoleIs(UserRegistrationRole.SuperUser);
  }

  get isClient() {
    return this.user?.idTokenClaims?.extension_IdNumber;
  }

  get isIntermediary() {
    return this.user?.idTokenClaims?.extension_ProducerNumber;
  }

  get home() {
    return '';
  }

  setAuthToken(token: string | null) {
    this.authTokenSubject.next(token);
  }

  /**
   * Ends the session at B2C and comes back to the backoffice root.
   *
   * The round trip through B2C is the point: the session lives in a cookie on the
   * b2clogin.com domain, which this origin cannot clear. Dropping our own tokens alone
   * leaves that cookie valid, so the next sign-in is answered from SSO without ever
   * prompting -- the user appears never to have signed out. `postLogoutRedirectUri`
   * is what keeps them from being stranded on B2C's own signed-out page.
   */
  async logOut() {
    // Captured before the local state is cleared: B2C needs it as the id_token_hint,
    // and the policy that issued it decides which authority can end the session.
    const account = this.account;

    this.user = undefined as any;
    // Leaving this set would keep azureId resolving to the signed-out user, and would
    // tell the 401 handler there is still a session to expire.
    this.account = undefined;
    this.bankProfileSubject.next(null);
    this.setAuthToken(null);
    this.$logout.next();

    if (environment.devAuth?.enabled) {
      // Reload rather than route: this service is already built around the old
      // impersonated user and would otherwise keep it.
      localStorage.removeItem('devUser');
      location.assign('/');
      return;
    }

    if (account) {
      try {
        // Navigates away, so nothing below runs on the happy path
        await lastValueFrom(
          this.msalService.logoutRedirect({
            account,
            authority: this.authorityForAccount(account),
            postLogoutRedirectUri: '/',
          }),
        );
        return;
      } catch (error) {
        console.error('Failed to sign out of the identity provider', error);
      }
    }

    // No account to sign out, or B2C refused: at least drop our own tokens so the
    // app does not carry on believing someone is signed in.
    try {
      await this.msalService.instance.clearCache();
    } catch (error) {
      console.error('Failed to clear the authentication cache', error);
    }

    await this.router.navigate(['/']);
  }

  /**
   * B2C refresh tokens are scoped to the policy that issued them, so an account created
   * through the sign-up flow cannot renew against the sign-in authority.
   */
  private authorityForAccount(account: AccountInfo): string {
    const claims = account.idTokenClaims as any;
    const policy: string | undefined = claims?.tfp ?? claims?.acr;

    return policy?.toLowerCase() === environment.b2cPolicies.names.signUp.toLowerCase()
      ? environment.b2cPolicies.authorities.signUp.authority
      : environment.b2cPolicies.authorities.signUpSignIn.authority;
  }

  async refreshToken(): Promise<string | null> {
    if (!this.account) return null;

    const tokenRequest: SilentRequest = {
      account: this.account,
      scopes: environment.apiConfig.scopes,
      authority: this.authorityForAccount(this.account),
    };

    try {
      const response =
        await this.msalService.instance.acquireTokenSilent(tokenRequest);
      this.user = {
        ...this.user,
        accessToken: response.accessToken,
        expiresOn: response.expiresOn,
      };
      this.setAuthToken(response.accessToken);
      return response.accessToken;
    } catch (error) {
      console.error('Error trying to refresh token silently', error);
      return null;
    }
  }

  private getAccount() {
    if (this.msalService.instance.getAllAccounts().length == 0) return;

    this.account = this.msalService.instance.getAllAccounts()[0];

    this.user = this.account as User;
    this.logIdentity('account resolved');
  }

  /**
   * Development aid: prints who the app thinks you are. `objectId (oid)` is the value
   * that has to match AzureId on the SuperUsers / Supervisors / BankEmployees row.
   * Silent in production builds.
   */
  logIdentity(stage = 'profile loaded'): void {
    if (!isDevMode()) return;

    // Re-inspect at any time from the console with: authUser()
    (window as any).authUser ??= () => this.logIdentity('on demand');

    const claims = (this.account?.idTokenClaims ?? {}) as Record<string, any>;
    const profile = this.bankProfile;
    const source = environment.devAuth?.enabled ? 'dev impersonation' : 'Azure B2C';
    const oid =
      this.account?.localAccountId ??
      claims['oid'] ??
      (environment.devAuth?.enabled
        ? localStorage.getItem('devUser') || environment.devAuth.defaultUser
        : null);

    console.log(this.account)

    console.log(
      `%c[auth]%c ${source} · ${stage} · ${profile ? `${profile.name} ${profile.lastName} (${profile.role})` : oid ?? 'anonymous'}`,
      'background:#9A0113;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold',
      'color:inherit',
      {
        objectId: oid,
        username: this.account?.username ?? null,
        policy: claims['tfp'] ?? claims['acr'] ?? null,
        bankProfile: profile,
        roles: {
          superuser: this.isSuperUser,
          global: this.isGlobal,
          regional: this.isRegional,
          supervisor: this.isSupervisor,
          manager: this.isManager,
          retailer: this.isRetailer,
        },
        account: this.account ?? null,
        idTokenClaims: claims,
      },
    );
  }

  private async getToken() {
    if (!this.msalService || !this.account) return;

    const tokenRequest: SilentRequest = {
      account: this.account,
      scopes: environment.apiConfig.scopes,
      authority: this.authorityForAccount(this.account),
    };

    try {
      const response =
        await this.msalService.instance.acquireTokenSilent(tokenRequest);
      this.user = {
        ...this.user,
        accessToken: response.accessToken,
        expiresOn: response.expiresOn,
      };
      this.setAuthToken(response.accessToken);
      // Always ask the server who this is. A bank user's personal B2C account may
      // carry customer claims (extension_IdNumber); skipping the profile for them
      // silently demoted real superusers to the register form.
      await this.getBankProfile();
    } catch (e) {
      // Don't sign the user out: a freshly signed-up account legitimately has no
      // usable silent token yet, and evicting them would abort the registration.
      console.error('Failed to get access token silently', e);
      this.setAuthToken(null);
      this.profileLoadedSubject.next(true);
    }
  }

  private async getBankProfile() {
    try {
      const profile = await lastValueFrom(
        this.httpClient.get<CurrentBankUserDto>('Employees/me'),
      );
      this.bankProfileSubject.next(profile);
    } catch (e: any) {
      this.bankProfileSubject.next(null);
      if (e?.status !== 404) {
        console.error('Failed to load bank user profile', e);
      }
    } finally {
      this.profileLoadedSubject.next(true);
      this.logIdentity();
    }
  }

}
